import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Collapse,
  Divider,
  Group,
  Loader,
  Menu,
  Modal,
  NumberInput,
  Popover,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { guardedStep, inversePatch, useUndo } from '../../ui/undo';
import { useParams } from 'react-router-dom';
import { addWorkingDays } from '@pst/core-scheduling';
import {
  ApiError,
  scheduleApi,
  type SchedTask,
  type ScheduleView,
  type WbsNode,
  undoPaths,
  commentApi,
} from '../../api';
import {
  RELATIONSHIPS,
  TASK_STATUS,
  UNIT_DAYS,
  type TaskStatusKey,
  formatDate,
  formatDateTime,
  relationshipLabel,
  relationshipShort,
} from '../../format';
import { ScheduleBoard } from './ScheduleBoard';
import type { ListHandlers, PredecessorLink } from './TaskList';
import { COLUMNS, loadOptionalColumns, loadOrientation, saveOptionalColumns, visibleColumns, type ColumnKey, type Orientation } from './columns';
import { PlanSettingsModal } from './PlanSettingsModal';
import { buildOutline, type OutlineRow } from './outline';
import { useContextMenu } from '../../ui/ContextMenu';
import { TaskCommentsModal } from './TaskComments';
import { inverseTaskBody } from './taskEdit';
import { condense, isSummaryRow, summaryIdOf } from './condense';
import { SummaryModal, type SummaryTarget } from './SummaryModal';
import { RollforwardModal } from './RollforwardModal';
import { ColumnsIcon, SideBySideIcon, StackedIcon } from './ToolbarIcons';
import { calendarOf, previewEdit } from './localSchedule';
import { baselineDisplay } from './baselineView';
import { usePageDownloads } from '../../export/downloads';

/**
 * The schedule of one project.
 *
 * Layout, from top to bottom, in the order of how often each part is needed:
 * one toolbar with everything that creates or changes the plan on the left and
 * everything that only affects the view on the right; then warnings, if any;
 * then the plan itself, taking all remaining height. Things needed rarely - the
 * legend, the list of relationships - are one click away instead of taking a
 * permanent line.
 */
export function SchedulePage(): JSX.Element {
  const { projectId = '' } = useParams();
  const exportBase = `/api/scheduling/projects/${projectId}/export`;
  usePageDownloads('schedule', [
    { key: 'print-a4', label: 'Druckansicht A4 quer', hint: 'ganzer Plan, dort „Als PDF speichern“', to: `/projekte/${projectId}/termine/druck?format=A4` },
    { key: 'print-a3', label: 'Druckansicht A3 quer', hint: 'mehr Platz für lange Pläne', to: `/projekte/${projectId}/termine/druck?format=A3` },
    { key: 'print-simple', label: 'Vereinfachter Plan, Druckansicht', hint: 'Zusammenfassungen als je ein Balken, z. B. für Bauherrschaft', to: `/projekte/${projectId}/termine/druck?format=A4&ansicht=vereinfacht` },
    { key: 'csv', label: 'Terminplan als CSV', hint: 'Vorgänge mit Terminen, Puffern und Abweichung', href: `${exportBase}/csv` },
    { key: 'mspdi', label: 'Terminplan als MSPDI-XML', hint: 'für Microsoft Project, Asta Powerproject u. a.', href: `${exportBase}/mspdi` },
  ]);
  const queryClient = useQueryClient();
  const undo = useUndo();
  const mobile = useMediaQuery('(max-width: 48em)') ?? false;

  const [taskModal, taskModal$] = useDisclosure();
  const [depModal, depModal$] = useDisclosure();
  const [phaseModal, phaseModal$] = useDisclosure();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [preferredOrientation, setOrientation] = useState<Orientation>(loadOrientation);
  const [notice, setNotice] = useState<string | null>(null);
  // The simplified plan (Zusammenfassungen) is a way of looking, kept per browser.
  const [simple, setSimpleState] = useState<boolean>(() => { try { return window.localStorage.getItem('pst.schedule.simple') === '1'; } catch { return false; } });
  const setSimple = (on: boolean): void => { setSimpleState(on); try { window.localStorage.setItem('pst.schedule.simple', on ? '1' : '0'); } catch { /* private window */ } };
  const [summaryTarget, setSummaryTarget] = useState<SummaryTarget | null>(null);
  // Claude making the simplified plan (ADR 0016); asked again every few seconds while it works.
  const summaryAi = useQuery({
    queryKey: ['summary-ai', projectId],
    queryFn: () => scheduleApi.summaryAi(projectId),
    enabled: simple,
    refetchInterval: (q) => (q.state.data?.pending ? 8000 : false),
  });
  const wasPending = useRef(false);
  useEffect(() => {
    const pending = summaryAi.data?.pending ?? false;
    if (wasPending.current && !pending) void queryClient.invalidateQueries({ queryKey: ['schedule', projectId] });
    wasPending.current = pending;
  }, [summaryAi.data?.pending, projectId, queryClient]);
  const askClaude = useMutation({
    mutationFn: () => scheduleApi.requestSummaryAi(projectId),
    onSuccess: (st) => queryClient.setQueryData(['summary-ai', projectId], st),
    onError: (error) => setNotice(error instanceof ApiError ? error.message : 'Claude konnte nicht beauftragt werden.'),
  });
  /**
   * The schedule as it would look if the bar being dragged were dropped here,
   * computed in the browser by the engine the server runs.
   */
  const [dragPreview, setDragPreview] = useState<ScheduleView | null>(null);

  // A phone is too narrow for two panes side by side; the list goes on top.
  const orientation: Orientation = mobile ? 'horizontal' : preferredOrientation;
  const scheduleKey = ['schedule', projectId];

  const plan = useQuery({ queryKey: ['plan', projectId], queryFn: () => scheduleApi.plan(projectId) });
  const wbs = useQuery({ queryKey: ['wbs', projectId], queryFn: () => scheduleApi.wbs(projectId) });
  const schedule = useQuery({ queryKey: scheduleKey, queryFn: () => scheduleApi.schedule(projectId) });
  const commentCounts = useQuery({ queryKey: ['task-comment-counts', projectId], queryFn: () => commentApi.counts(projectId) });
  const [commentTask, setCommentTask] = useState<{ id: string; code: string; name: string } | null>(null);
  const deps = useQuery({
    queryKey: ['deps', projectId],
    queryFn: () => scheduleApi.dependencies(projectId),
  });
  const activeBaseline = useQuery({
    queryKey: ['baseline', projectId],
    queryFn: () => scheduleApi.activeBaseline(projectId),
  });

  const refresh = async (): Promise<void> => {
    await Promise.all(
      [scheduleKey, ['deps', projectId], ['wbs', projectId], ['plan', projectId],
        ['baseline', projectId], ['baselines', projectId]].map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    );
  };

  const failed = (error: unknown, fallback: string): void =>
    setNotice(error instanceof ApiError ? error.message : fallback);

  /** Edits a task, showing the browser's own calculation before the answer. */
  const editTask = useMutation({
    mutationFn: (input: { taskId: string; body: Record<string, unknown> }) =>
      scheduleApi.updateTask(projectId, input.taskId, input.body),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: scheduleKey });
      const previous = queryClient.getQueryData<ScheduleView>(scheduleKey);
      const before = previous?.tasks.find((t) => t.id === input.taskId);
      const back = before ? inverseTaskBody(before, input.body) : {};
      if (previous && plan.data) {
        const preview = previewEdit(plan.data, previous, deps.data ?? [], input.taskId, input.body);
        if (preview) queryClient.setQueryData(scheduleKey, preview);
      }
      return { previous, back, code: before?.code };
    },
    // Expected are the values as the server stored them - a finish date comes back as a duration.
    onSuccess: (saved, input, context) => {
      if (context.code !== undefined) undo.record(guardedStep(`Vorgang ${context.code}`, 'PATCH', undoPaths.task(projectId, input.taskId), context.back, saved as unknown as Record<string, unknown>));
    },
    onError: (error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(scheduleKey, context.previous);
      failed(error, 'Die Änderung konnte nicht gespeichert werden.');
    },
    onSettled: refresh,
  });

  const editGroup = useMutation({
    mutationFn: (input: { groupId: string; body: Record<string, unknown> }) => {
      const before = wbs.data?.find((w) => w.id === input.groupId);
      const step = before
        ? guardedStep(`Phase ${before.code}`, 'PATCH', undoPaths.wbs(projectId, input.groupId), inversePatch(input.body, before as unknown as Record<string, unknown>), input.body)
        : null;
      return undo.recordAfter(scheduleApi.updateWbs(projectId, input.groupId, input.body), step);
    },
    onSuccess: refresh,
    onError: (error) => failed(error, 'Die Phase konnte nicht geändert werden.'),
  });
  const removeTask = useMutation({
    mutationFn: (taskId: string) => scheduleApi.deleteTask(projectId, taskId),
    onSuccess: refresh,
    onError: (error) => failed(error, 'Der Vorgang konnte nicht gelöscht werden.'),
  });
  const removeGroup = useMutation({
    mutationFn: (groupId: string) => scheduleApi.deleteWbs(projectId, groupId),
    onSuccess: refresh,
    onError: (error) => failed(error, 'Die Phase konnte nicht gelöscht werden.'),
  });
  const summaryOf = (id: string) => schedule.data?.summaries.find((s) => s.id === summaryIdOf(id));
  const renameSummary = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => scheduleApi.updateSummary(projectId, id, { name }),
    onSuccess: refresh,
    onError: (error) => failed(error, 'Die Zusammenfassung konnte nicht umbenannt werden.'),
  });
  const dissolveSummary = useMutation({
    mutationFn: (id: string) => scheduleApi.deleteSummary(projectId, id),
    onSuccess: refresh,
    onError: (error) => failed(error, 'Die Zusammenfassung konnte nicht aufgelöst werden.'),
  });
  const askDissolve = (id: string): void => {
    const s = schedule.data?.summaries.find((x) => x.id === id);
    if (s && window.confirm(`Zusammenfassung „${s.name}“ auflösen? Die Vorgänge bleiben, wie sie sind, und erscheinen im vereinfachten Plan wieder einzeln.`)) dissolveSummary.mutate(id);
  };
  /** Every task below a phase, also in its sub-phases. */
  const tasksBelow = (groupId: string): string[] => {
    const all = schedule.data;
    if (!all) return [];
    const inside = new Set([groupId]);
    let grew = true;
    while (grew) { grew = false; for (const g of all.groups) if (g.parentId && inside.has(g.parentId) && !inside.has(g.id)) { inside.add(g.id); grew = true; } }
    return all.tasks.filter((t) => t.wbsNodeId && inside.has(t.wbsNodeId)).map((t) => t.id);
  };
  const setProjectStart = useMutation({
    mutationFn: (date: string) => scheduleApi.updatePlan(projectId, { startDate: date }),
    onSuccess: refresh,
    onError: (error) => failed(error, 'Der Projektbeginn konnte nicht geändert werden.'),
  });

  const detailed = dragPreview ?? schedule.data;
  const condensed = useMemo(
    () => (simple && detailed && plan.data ? condense(detailed, deps.data ?? [], calendarOf(plan.data)) : null),
    [simple, detailed, plan.data, deps.data],
  );
  const shown = condensed?.schedule ?? detailed;
  const shownDeps = condensed?.dependencies ?? deps.data ?? [];
  const rows = useMemo(
    () => buildOutline(shown?.groups ?? [], shown?.tasks ?? [], collapsed),
    [shown, collapsed],
  );
  const comparison = useMemo(
    () => (plan.data && shown ? baselineDisplay(plan.data, shown.tasks, activeBaseline.data) : null),
    [plan.data, shown, activeBaseline.data],
  );
  const predecessors = useMemo(() => {
    const codeOf = new Map((shown?.tasks ?? []).map((task) => [task.id, task.code]));
    const map = new Map<string, PredecessorLink[]>();
    for (const dependency of shownDeps) {
      const list = map.get(dependency.successorId) ?? [];
      list.push({
        code: codeOf.get(dependency.predecessorId) ?? '?',
        type: dependency.type,
        lagDays: dependency.lagDays,
      });
      map.set(dependency.successorId, list);
    }
    return map;
  }, [shownDeps, shown]);
  const [optionalColumns, setOptionalColumns] = useState<Set<ColumnKey>>(loadOptionalColumns);
  const toggleColumn = (key: ColumnKey): void =>
    setOptionalColumns((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      saveOptionalColumns(next);
      return next;
    });
  const [settingsOpen, settings$] = useDisclosure();
  const [taskPreset, setTaskPreset] = useState<TaskPreset | null>(null);
  const [phasePreset, setPhasePreset] = useState<string | null>(null);
  const closeTaskModal = (): void => {
    taskModal$.close();
    setTaskPreset(null);
  };
  const closePhaseModal = (): void => {
    phaseModal$.close();
    setPhasePreset(null);
  };
  const duplicate = useMutation({
    mutationFn: async (task: SchedTask) => {
      const codes = new Set((schedule.data?.tasks ?? []).map((t) => t.code));
      let n = 2;
      while (codes.has(`${task.code}-${n}`)) n++;
      const copy = await scheduleApi.createTask(projectId, {
        code: `${task.code}-${n}`,
        name: `${task.name} (Kopie)`,
        durationDays: task.durationDays,
        wbsNodeId: task.wbsNodeId,
        sortOrder: task.sortOrder,
        note: task.note,
        color: task.color,
        owner: task.owner,
        position: task.position,
        deliverable: task.deliverable,
        deadline: task.deadline,
      });
      if (task.constraintType === 'startNoEarlierThan' && task.constraintDate) {
        await scheduleApi.updateTask(projectId, copy.id, { startDate: task.constraintDate });
      }
    },
    onSettled: refresh,
  });
  const rowMenu = useContextMenu<OutlineRow>((row, close) => {
    const run = (action: () => void) => () => {
      close();
      action();
    };
    if (row.kind === 'group') {
      const group = row.group;
      return (
        <>
          <Menu.Label>{group.code} {group.name}</Menu.Label>
          <Menu.Item onClick={run(() => { setTaskPreset({ wbsNodeId: group.id }); taskModal$.open(); })}>Vorgang in dieser Phase …</Menu.Item>
          <Menu.Item onClick={run(() => { setPhasePreset(group.id); phaseModal$.open(); })}>Unterphase anlegen …</Menu.Item>
          <Menu.Item onClick={run(() => setCollapsed((previous) => {
            const next = new Set(previous);
            if (next.has(group.id)) next.delete(group.id);
            else next.add(group.id);
            return next;
          }))}>
            {collapsed.has(group.id) ? 'Aufklappen' : 'Zuklappen'}
          </Menu.Item>
          <Menu.Item onClick={run(() => setSummaryTarget({ mode: 'create', name: group.name, taskIds: tasksBelow(group.id) }))}>Phase zusammenfassen …</Menu.Item>
          <Menu.Divider />
          <Menu.Item color="red" onClick={run(() => removeGroup.mutate(group.id))}>Phase löschen</Menu.Item>
        </>
      );
    }
    const task = row.task;
    if (isSummaryRow(task.id)) {
      const s = summaryOf(task.id);
      return (
        <>
          <Menu.Label>Zusammenfassung {task.code}</Menu.Label>
          {s && <Menu.Item onClick={run(() => setSummaryTarget({ mode: 'edit', summary: s }))}>Name und Vorgänge …</Menu.Item>}
          <Menu.Item onClick={run(() => setSimple(false))}>Im Detailplan zeigen</Menu.Item>
          <Menu.Divider />
          {s && <Menu.Item color="red" onClick={run(() => askDissolve(s.id))}>Zusammenfassung auflösen …</Menu.Item>}
        </>
      );
    }
    const inSummary = task.summaryId ? schedule.data?.summaries.find((x) => x.id === task.summaryId) : undefined;
    return (
      <>
        <Menu.Label>{task.code} {task.name.length > 40 ? `${task.name.slice(0, 40)} …` : task.name}</Menu.Label>
        <Menu.Label>Status</Menu.Label>
        <Group gap={4} px="xs" pb={4}>
          {(Object.keys(TASK_STATUS) as TaskStatusKey[]).map((key) => (
            <Badge
              key={key}
              component="button"
              color={TASK_STATUS[key].color}
              variant={task.status === key ? 'filled' : 'light'}
              style={{ cursor: 'pointer', textTransform: 'none' }}
              onClick={run(() => key !== task.status && editTask.mutate({ taskId: task.id, body: { status: key } }))}
            >
              {TASK_STATUS[key].label}
            </Badge>
          ))}
        </Group>
        <Menu.Divider />
        <Menu.Item onClick={run(() => setCommentTask(task))}>Kommentare{commentCounts.data?.[task.id] ? ` (${commentCounts.data[task.id]})` : ''} …</Menu.Item>
        <Menu.Divider />
        <Menu.Item onClick={run(() => { setTaskPreset({ wbsNodeId: task.wbsNodeId, predecessorId: task.id, predecessorCode: task.code }); taskModal$.open(); })}>
          Nachfolger anlegen …
        </Menu.Item>
        <Menu.Item onClick={run(() => { setTaskPreset({ wbsNodeId: task.wbsNodeId }); taskModal$.open(); })}>Vorgang in derselben Phase …</Menu.Item>
        <Menu.Item onClick={run(() => duplicate.mutate(task))}>Duplizieren</Menu.Item>
        <Menu.Divider />
        <Menu.Label>Vereinfachter Plan</Menu.Label>
        <Menu.Item onClick={run(() => setSummaryTarget({ mode: 'create', name: '', taskIds: [task.id] }))}>Zusammenfassen mit …</Menu.Item>
        {(schedule.data?.summaries ?? []).filter((x) => x.id !== task.summaryId).slice(0, 8).map((x) => (
          <Menu.Item key={x.id} onClick={run(() => editTask.mutate({ taskId: task.id, body: { summaryId: x.id } }))}>In „{x.name.length > 30 ? `${x.name.slice(0, 30)} …` : x.name}“</Menu.Item>
        ))}
        {inSummary && <Menu.Item onClick={run(() => editTask.mutate({ taskId: task.id, body: { summaryId: null } }))}>Aus „{inSummary.name}“ nehmen</Menu.Item>}
        {task.durationDays === 0 ? (
          <Menu.Item onClick={run(() => editTask.mutate({ taskId: task.id, body: { durationDays: 1 } }))}>In Vorgang umwandeln (1 {UNIT_DAYS})</Menu.Item>
        ) : (
          <Menu.Item onClick={run(() => editTask.mutate({ taskId: task.id, body: { durationDays: 0 } }))}>In Meilenstein umwandeln</Menu.Item>
        )}
        {task.constraintType !== null && (
          <Menu.Item onClick={run(() => editTask.mutate({ taskId: task.id, body: { startDate: null } }))}>Terminvorgabe aufheben</Menu.Item>
        )}
        <Menu.Divider />
        <Menu.Item color="red" onClick={run(() => removeTask.mutate(task.id))}>Vorgang löschen</Menu.Item>
      </>
    );
  });
  const columns = useMemo(
    () => visibleColumns({ mobile, hasBaseline: comparison !== null, optional: optionalColumns }),
    [mobile, comparison, optionalColumns],
  );

  const detailHandlers: ListHandlers = {
    onRenameTask: (taskId, name) => editTask.mutate({ taskId, body: { name } }),
    onRenameGroup: (groupId, name) => editGroup.mutate({ groupId, body: { name } }),
    onRecodeTask: (taskId, code) => editTask.mutate({ taskId, body: { code } }),
    onRecodeGroup: (groupId, code) => editGroup.mutate({ groupId, body: { code } }),
    onChangeDuration: (taskId, durationDays) => editTask.mutate({ taskId, body: { durationDays } }),
    onChangeStart: (taskId, startDate) => editTask.mutate({ taskId, body: { startDate } }),
    onChangeFinish: (taskId, finishDate) => editTask.mutate({ taskId, body: { finishDate } }),
    onClearConstraint: (taskId) => editTask.mutate({ taskId, body: { startDate: null } }),
    onChangeFloat: (taskId, desired) => {
      // Float is a result, not an input. What can be done is to SPEND it:
      // delay the task until only the wanted amount is left. More than the
      // network allows cannot be conjured, and is refused in plain words.
      const task = schedule.data?.tasks.find((entry) => entry.id === taskId);
      if (!task || task.totalFloat === null || !task.earlyStart || !plan.data) return;
      const spend = task.totalFloat - desired;
      if (spend <= 0) {
        setNotice(
          'Mehr Puffer, als der Netzplan hergibt, lässt sich nicht einstellen. ' +
            'Kürzen Sie den Vorgang oder lösen Sie eine Anordnungsbeziehung.',
        );
        return;
      }
      const start = addWorkingDays(calendarOf(plan.data), task.earlyStart, spend);
      editTask.mutate({ taskId, body: { startDate: start } });
    },
    onColorTask: (taskId, color) => editTask.mutate({ taskId, body: { color } }),
    onColorGroup: (groupId, color) => editGroup.mutate({ groupId, body: { color } }),
    onDeleteTask: (taskId) => removeTask.mutate(taskId),
    onDeleteGroup: (groupId) => removeGroup.mutate(groupId),
    onPatchTask: (taskId, body) => editTask.mutate({ taskId, body }),
    onRowContextMenu: (event, row) => rowMenu.open(event, row),
    commentCount: (taskId) => commentCounts.data?.[taskId] ?? 0,
    onOpenComments: (taskId) => {
      const task = schedule.data?.tasks.find((entry) => entry.id === taskId);
      if (task) setCommentTask(task);
    },
  };
  // On the simplified plan a summary bar is not a task: renaming renames the
  // summary, the cross dissolves it, everything else about it is read only.
  const skip = (id: string): boolean => isSummaryRow(id);
  const handlers: ListHandlers = {
    ...detailHandlers,
    onRenameTask: (id, name) => (skip(id) ? renameSummary.mutate({ id: summaryIdOf(id), name }) : detailHandlers.onRenameTask(id, name)),
    onRecodeTask: (id, code) => { if (!skip(id)) detailHandlers.onRecodeTask(id, code); },
    onChangeDuration: (id, d) => { if (!skip(id)) detailHandlers.onChangeDuration(id, d); },
    onChangeStart: (id, d) => { if (!skip(id)) detailHandlers.onChangeStart(id, d); },
    onChangeFinish: (id, d) => { if (!skip(id)) detailHandlers.onChangeFinish(id, d); },
    onClearConstraint: (id) => { if (!skip(id)) detailHandlers.onClearConstraint(id); },
    onChangeFloat: (id, f) => { if (!skip(id)) detailHandlers.onChangeFloat(id, f); },
    onColorTask: (id, c) => { if (!skip(id)) detailHandlers.onColorTask(id, c); },
    onDeleteTask: (id) => (skip(id) ? askDissolve(summaryIdOf(id)) : detailHandlers.onDeleteTask(id)),
    onPatchTask: (id, body) => { if (!skip(id)) detailHandlers.onPatchTask(id, body); },
    commentCount: (id) => (skip(id) ? 0 : detailHandlers.commentCount?.(id) ?? 0),
  };

  if (plan.isPending || schedule.isPending) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }
  if (plan.isError || schedule.isError) {
    const error = plan.error ?? schedule.error;
    return (
      <Alert color="red" title="Terminplan nicht verfügbar">
        {error instanceof ApiError && error.status === 403
          ? 'Ihre Rolle hat keinen Zugriff auf die Terminplanung.'
          : 'Der Terminplan konnte nicht geladen werden.'}
      </Alert>
    );
  }

  const data = schedule.data;
  const busy =
    editTask.isPending || editGroup.isPending || removeTask.isPending ||
    removeGroup.isPending || setProjectStart.isPending;
  const create = { task: taskModal$.open, phase: phaseModal$.open, dependency: depModal$.open };

  return (
    <Stack gap="xs" style={{ flex: 1, minHeight: 0 }}>
      <Card withBorder p={0} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {/* toolbar: changing the plan on the left, viewing it on the right */}
        <Group
          justify="space-between"
          wrap="nowrap"
          gap="xs"
          px="xs"
          py={6}
          // On a phone the toolbar is wider than the screen; it scrolls
          // sideways instead of cutting off the settings at the right.
          style={{ borderBottom: '1px solid var(--mantine-color-default-border)', overflowX: 'auto' }}
        >
          {mobile ? (
            <Group gap={4} wrap="nowrap">
              <CreateMenu onCreate={create} />
              <ToolsMenu projectId={projectId} onDone={refresh} onNotice={setNotice} />
            </Group>
          ) : (
            <Group gap={4} wrap="nowrap">
              <Button size="xs" variant="light" onClick={create.task}>+ Vorgang</Button>
              <Button size="xs" variant="subtle" onClick={create.phase}>+ Phase</Button>
              <Button size="xs" variant="subtle" onClick={create.dependency}>+ Beziehung</Button>
              <Divider orientation="vertical" mx={4} />
              <TemplateButton projectId={projectId} onDone={refresh} onNotice={setNotice} />
              <BaselineMenu projectId={projectId} onDone={refresh} onNotice={setNotice} />
            </Group>
          )}

          <Group gap="xs" wrap="nowrap">
            {busy && <Loader size="xs" aria-label="Wird gespeichert" />}
            {/* The project: when it begins, when it ends. */}
            <Tooltip label="Projektbeginn – ab hier rechnet der Plan">
              <input
                type="date"
                aria-label="Projektbeginn"
                value={plan.data.startDate}
                onChange={(event) => {
                  if (event.currentTarget.value) setProjectStart.mutate(event.currentTarget.value);
                }}
                style={{
                  border: '1px solid var(--mantine-color-default-border)',
                  borderRadius: 'var(--mantine-radius-sm)',
                  padding: '2px 6px',
                  background: 'var(--mantine-color-body)',
                  color: 'var(--mantine-color-text)',
                }}
              />
            </Tooltip>
            <Text size="xs" c="dimmed" visibleFrom="xl" style={{ whiteSpace: 'nowrap' }} title="Projektende nach der Berechnung">
              bis {formatDate(data.earliestFinish)}
            </Text>
            <Divider orientation="vertical" mx={2} />
            {/* The view: detailed or simplified, arrangement, columns, legend, settings. */}
            <Tooltip label="Vereinfacht: Zusammenfassungen als je ein Balken (Rechtsklick auf Vorgang oder Phase › Zusammenfassen)">
              <SegmentedControl
                size="xs"
                value={simple ? 'simple' : 'detail'}
                onChange={(value) => setSimple(value === 'simple')}
                data={[{ value: 'detail', label: 'Detailliert' }, { value: 'simple', label: 'Vereinfacht' }]}
                aria-label="Detaillierter oder vereinfachter Plan"
              />
            </Tooltip>
            {simple && (
              <Tooltip multiline w={300} label={summaryAi.data?.available === false
                ? `Die KI ist gerade nicht verfügbar: ${summaryAi.data.reason ?? 'Grund unbekannt.'}`
                : 'Vereinfachte Version mit Claude erstellen: Claude fasst die Vorgänge selbst zusammen und ersetzt die bisherigen Zusammenfassungen. Der Detailplan bleibt unverändert.'}>
                {/* A disabled button fires no mouse events; the span carries the tooltip. */}
                <span>
                  <Button
                    size="compact-xs" variant="light"
                    aria-label="Vereinfachte Version mit Claude erstellen"
                    loading={askClaude.isPending || summaryAi.data?.pending}
                    disabled={summaryAi.data?.available === false}
                    onClick={() => {
                      if ((schedule.data?.summaries.length ?? 0) === 0 || window.confirm('Claude erstellt eine neue vereinfachte Fassung und ersetzt dabei die bisherigen Zusammenfassungen. Fortfahren?')) askClaude.mutate();
                    }}
                  >
                    ✦ Claude
                  </Button>
                </span>
              </Tooltip>
            )}
            {!mobile && (
              <Tooltip label={preferredOrientation === 'vertical' ? 'Liste und Balken nebeneinander – klicken für übereinander' : 'Liste und Balken übereinander – klicken für nebeneinander'}>
                <ActionIcon
                  variant="subtle" color="gray"
                  aria-label="Anordnung von Liste und Balkendiagramm wechseln"
                  onClick={() => setOrientation(preferredOrientation === 'vertical' ? 'horizontal' : 'vertical')}
                >
                  {preferredOrientation === 'vertical' ? <SideBySideIcon /> : <StackedIcon />}
                </ActionIcon>
              </Tooltip>
            )}
            {!mobile && (
              <Menu position="bottom-end" closeOnItemClick={false}>
                <Menu.Target>
                  <Tooltip label="Zusätzliche Spalten">
                    <ActionIcon variant="subtle" color="gray" aria-label="Zusätzliche Spalten wählen"><ColumnsIcon /></ActionIcon>
                  </Tooltip>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Zusätzliche Spalten</Menu.Label>
                  {COLUMNS.filter((column) => column.optional).map((column) => (
                    <Menu.Item
                      key={column.key}
                      onClick={() => toggleColumn(column.key)}
                      leftSection={<span style={{ width: 14, display: 'inline-block' }}>{optionalColumns.has(column.key) ? '✓' : ''}</span>}
                    >
                      {column.title ?? column.label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            )}
            <LegendButton showBaseline={comparison !== null} baselineName={comparison?.name} />
            <Tooltip label="Plan und Kalender einstellen">
              <ActionIcon variant="subtle" color="gray" onClick={settings$.open} aria-label="Plan und Kalender einstellen">
                {'⚙'}
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {simple && (summaryAi.data?.pending || summaryAi.data?.note) && (
          <Alert color={summaryAi.data.pending ? 'brand' : 'gray'} py={6} mx="xs" mt={6}>
            {summaryAi.data.pending ? 'Claude erstellt die vereinfachte Fassung – meist in ein bis wenigen Minuten; ist die Tagesgrenze der KI-Aufträge erreicht, am nächsten Tag. Die Seite aktualisiert sich von selbst.' : summaryAi.data.note}
          </Alert>
        )}
        {(notice || data.stale || data.behindTarget) && (
          <Stack gap={4} p="xs" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
            {notice && (
              <Alert color="yellow" withCloseButton onClose={() => setNotice(null)} py={6}>
                {notice}
              </Alert>
            )}
            {data.stale && (
              <Alert color="yellow" title="Termine nicht aktuell" py={6}>
                Seit der letzten Änderung sind die Termine nicht neu berechnet worden – die angezeigten Daten können abweichen.
              </Alert>
            )}
            {data.behindTarget && (
              <Alert color="red" title="Zieltermin nicht erreichbar" py={6}>
                Frühestes Projektende {formatDate(data.earliestFinish)}, Zieltermin{' '}
                {formatDate(data.targetFinish)}. Vorgänge mit negativem Puffer stehen rot.
              </Alert>
            )}
          </Stack>
        )}

        {rows.length === 0 ? (
          <Stack align="center" gap="sm" p="xl">
            <Text c="dimmed">Noch keine Vorgänge.</Text>
            <Group gap="xs">
              <TemplateButton projectId={projectId} onDone={refresh} onNotice={setNotice} prominent />
              <Button variant="light" onClick={create.task}>+ Vorgang</Button>
            </Group>
          </Stack>
        ) : (
          <ScheduleBoard
            rows={rows}
            groups={shown?.groups ?? data.groups}
            dependencies={shownDeps}
            collapsed={collapsed}
            orientation={orientation}
            onToggleCollapse={(groupId) =>
              setCollapsed((previous) => {
                const next = new Set(previous);
                if (next.has(groupId)) next.delete(groupId);
                else next.add(groupId);
                return next;
              })
            }
            handlers={handlers}
            onDragPreview={(taskId, edit) => {
              if (!plan.data || !schedule.data || isSummaryRow(taskId)) return;
              setDragPreview(previewEdit(plan.data, schedule.data, deps.data ?? [], taskId, edit));
            }}
            onDragCommit={(taskId, edit) => {
              setDragPreview(null);
              if (isSummaryRow(taskId)) return;
              editTask.mutate({ taskId, body: edit });
            }}
            onDragCancel={() => setDragPreview(null)}
            baseline={comparison}
            columns={columns}
            predecessors={predecessors}
          />
        )}

        <Group
          justify="space-between"
          px="xs"
          py={4}
          style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}
        >
          <Text size="xs" c="dimmed">
            {data.tasks.length} Vorgänge ·{' '}
            <Text span size="xs" c="dimmed" td="underline" style={{ cursor: 'pointer' }} onClick={settings$.open}>
              Kalender {plan.data.calendar.bundesland}
            </Text>
            {data.calculatedAt ? ` · berechnet ${formatDateTime(data.calculatedAt)}` : ''}
            {simple && ` · vereinfacht: ${data.summaries.length} Zusammenfassung${data.summaries.length === 1 ? '' : 'en'}${data.summaries.length === 0 ? ' – Rechtsklick auf einen Vorgang oder eine Phase › Zusammenfassen' : ', gerechnet wird mit allen Vorgängen'}`}
          </Text>
          <DependencySection projectId={projectId} tasks={data.tasks} onDone={refresh} />
        </Group>
      </Card>

      <PlanSettingsModal projectId={projectId} plan={plan.data} opened={settingsOpen} onClose={settings$.close} onDone={refresh} />
      <PhaseModal projectId={projectId} opened={phaseModal} onClose={closePhaseModal} onDone={refresh} wbsNodes={wbs.data ?? []} presetParent={phasePreset} />
      <TaskModal projectId={projectId} opened={taskModal} onClose={closeTaskModal} onDone={refresh} wbsNodes={wbs.data ?? []} preset={taskPreset} />
      {rowMenu.menu}
      <TaskCommentsModal projectId={projectId} task={commentTask} onClose={() => setCommentTask(null)} />
      <SummaryModal projectId={projectId} target={summaryTarget} tasks={data.tasks} summaries={data.summaries} onClose={() => setSummaryTarget(null)} onDone={refresh} />
      <DependencyModal projectId={projectId} opened={depModal} onClose={depModal$.close} onDone={refresh} tasks={data.tasks} />
    </Stack>
  );
}

// --- toolbar parts -------------------------------------------------------

/** On a phone, the three "new" buttons fold into one. */
function CreateMenu({
  onCreate,
}: {
  onCreate: { task: () => void; phase: () => void; dependency: () => void };
}): JSX.Element {
  return (
    <Menu position="bottom-start">
      <Menu.Target>
        <Button size="xs" variant="light">+ Neu</Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={onCreate.task}>Vorgang</Menu.Item>
        <Menu.Item onClick={onCreate.phase}>Phase</Menu.Item>
        <Menu.Item onClick={onCreate.dependency}>Anordnungsbeziehung</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

/** On a phone, template, baseline and export fold into one menu. */
function ToolsMenu({
  projectId,
  onDone,
  onNotice,
}: {
  projectId: string;
  onDone: () => Promise<void>;
  onNotice: (message: string) => void;
}): JSX.Element {
  return (
    <Group gap={4} wrap="nowrap">
      <TemplateButton projectId={projectId} onDone={onDone} onNotice={onNotice} />
      <BaselineMenu projectId={projectId} onDone={onDone} onNotice={onNotice} />
    </Group>
  );
}

/** The legend, one click away instead of a permanent line under the plan. */
function LegendButton({
  showBaseline,
  baselineName,
}: {
  showBaseline: boolean;
  baselineName?: string;
}): JSX.Element {
  const strip = (colour: string, label: string, opacity = 1): JSX.Element => (
    <Group gap={8} wrap="nowrap">
      <svg width={20} height={14} aria-hidden>
        <rect width={20} height={9} rx={2} fill="var(--mantine-color-brand-6)" opacity={0.95} />
        <rect y={10} width={20} height={4} rx={1} fill={colour} opacity={opacity} />
      </svg>
      <Text size="xs">{label}</Text>
    </Group>
  );
  return (
    <Popover position="bottom-end" width={280} withArrow shadow="md">
      <Popover.Target>
        <Tooltip label="Legende" openDelay={400}>
          <ActionIcon variant="subtle" color="gray" aria-label="Legende anzeigen">?</ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap={6}>
          <Text size="xs" fw={600}>Streifen unter dem Balken</Text>
          {strip('#d9332a', 'kein Puffer – kritischer Pfad')}
          {strip('#e8590c', 'negativer Puffer – Zieltermin gefährdet')}
          {strip('#2f9e44', 'Puffer vorhanden', 0.55)}
          <Divider my={2} />
          <Group gap={8} wrap="nowrap">
            <svg width={20} height={14} aria-hidden>
              <polygon points="10,0 17,7 10,14 3,7" fill="var(--mantine-color-brand-6)" />
            </svg>
            <Text size="xs">Meilenstein (Dauer 0)</Text>
          </Group>
          <Group gap={8} wrap="nowrap">
            <svg width={20} height={10} aria-hidden>
              <path d="M0,0 L20,0 L20,9 L16,4 L4,4 L0,9 Z" fill="var(--mantine-color-dimmed)" />
            </svg>
            <Text size="xs">Phase – Zeitraum ihrer Vorgänge</Text>
          </Group>
          {showBaseline && (
            <Group gap={8} wrap="nowrap">
              <svg width={20} height={12} aria-hidden>
                <rect width={20} height={3} rx={1} fill="var(--mantine-color-gray-5)" />
                <rect y={5} width={20} height={7} rx={2} fill="var(--mantine-color-brand-6)" />
              </svg>
              <Text size="xs">grau darüber: Basisplan „{baselineName}"</Text>
            </Group>
          )}
          <Text size="xs" c="dimmed">gestrichelte Linie: heute</Text>
          <Text size="xs" c="dimmed">unterstrichenes Datum: Terminvorgabe</Text>
          <Divider my={2} />
          <Text size="xs" c="dimmed">
            Zellen anklicken oder mit Tab ansteuern und Enter drücken, um sie zu
            ändern. Balken ziehen verschiebt, die rechte Kante ändert die Dauer.
          </Text>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

/** Freeze the plan, and choose which frozen plan to compare against. */
function BaselineMenu({
  projectId,
  onDone,
  onNotice,
}: {
  projectId: string;
  onDone: () => Promise<void>;
  onNotice: (message: string) => void;
}): JSX.Element {
  const [naming, naming$] = useDisclosure();
  const [rolling, rolling$] = useDisclosure();
  const [menuOpen, setMenuOpen] = useState(false);
  const queryClient = useQueryClient();
  const canBaseline = queryClient.getQueryData<{ permissions: string[] }>(['me'])?.permissions.includes('scheduling.baseline') ?? false;
  const [name, setName] = useState('');
  const list = useQuery({ queryKey: ['baselines', projectId], queryFn: () => scheduleApi.baselines(projectId) });

  const failed = (error: unknown): void =>
    onNotice(
      error instanceof ApiError && error.status === 403
        ? 'Basispläne festhalten und umschalten darf nur die Projektleitung.'
        : error instanceof ApiError
          ? error.message
          : 'Der Basisplan konnte nicht geändert werden.',
    );

  const create = useMutation({
    mutationFn: () => scheduleApi.createBaseline(projectId, name),
    onSuccess: async () => {
      setName('');
      naming$.close();
      await onDone();
    },
    onError: failed,
  });
  const activate = useMutation({ mutationFn: (id: string) => scheduleApi.activateBaseline(projectId, id), onSuccess: onDone, onError: failed });
  const deactivate = useMutation({ mutationFn: () => scheduleApi.deactivateBaseline(projectId), onSuccess: onDone, onError: failed });
  const remove = useMutation({ mutationFn: (id: string) => scheduleApi.deleteBaseline(projectId, id), onSuccess: onDone, onError: failed });

  const baselines = list.data ?? [];
  const active = baselines.find((entry) => entry.isActive);

  return (
    <>
      {/* Controlled, so opening the naming dialog closes the menu - it stayed open on top of the dialog. */}
      <Menu position="bottom-start" width={300} closeOnItemClick={false} opened={menuOpen} onChange={setMenuOpen}>
        <Menu.Target>
          <Button
            size="xs" variant={active ? 'light' : 'subtle'}
            title={active ? `Stand festhalten, fortschreiben, vergleichen – verglichen wird mit „${active.name}“` : 'Stand festhalten, fortschreiben, vergleichen'}
            leftSection={active ? <Text span size="xs" c="brand" aria-hidden>●</Text> : undefined}
          >
            Stand
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={() => { setMenuOpen(false); naming$.open(); }}>Aktuellen Stand festhalten …</Menu.Item>
          <Menu.Item onClick={() => { setMenuOpen(false); rolling$.open(); }}>Auf heute fortschreiben …</Menu.Item>
          {baselines.length > 0 && (
            <>
              <Menu.Divider />
              <Menu.Label>Vergleichen mit</Menu.Label>
            </>
          )}
          {baselines.map((entry) => (
            <Menu.Item
              key={entry.id}
              onClick={() => activate.mutate(entry.id)}
              leftSection={<Text size="xs" c={entry.isActive ? 'brand' : 'transparent'}>{'●'}</Text>}
              rightSection={
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="critical"
                  aria-label={`Basisplan ${entry.name} löschen`}
                  onClick={(event) => {
                    event.stopPropagation();
                    remove.mutate(entry.id);
                  }}
                >
                  {'×'}
                </ActionIcon>
              }
            >
              <Text size="sm">{entry.name}</Text>
              <Text size="xs" c="dimmed">
                {formatDateTime(entry.createdAt)} · {entry.taskCount} Vorgänge
              </Text>
            </Menu.Item>
          ))}
          {active && (
            <>
              <Menu.Divider />
              <Menu.Item onClick={() => deactivate.mutate()}>Keinen Vergleich anzeigen</Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>

      <RollforwardModal projectId={projectId} opened={rolling} onClose={rolling$.close} canTakeOver={canBaseline} />
      <Modal opened={naming} onClose={naming$.close} title="Basisplan festhalten">
        <form onSubmit={(event) => { event.preventDefault(); create.mutate(); }}>
          <Stack>
            <Text size="sm" c="dimmed">
              Friert die jetzigen Termine aller Vorgänge ein. Spätere Änderungen
              erscheinen dagegen als Abweichung.
            </Text>
            <TextInput
              label="Bezeichnung"
              placeholder="z. B. Vertragstermine"
              required
              data-autofocus
              value={name}
              onChange={(event) => setName(event.currentTarget.value)}
            />
            <Button type="submit" loading={create.isPending} disabled={!name.trim()}>Festhalten</Button>
          </Stack>
        </form>
      </Modal>
    </>
  );
}

/** Ready-made outlines. Prominent in an empty plan, a small button otherwise. */
function TemplateButton({
  projectId,
  onDone,
  onNotice,
  prominent = false,
}: {
  projectId: string;
  onDone: () => Promise<void>;
  onNotice: (message: string) => void;
  prominent?: boolean;
}): JSX.Element | null {
  const templates = useQuery({ queryKey: ['templates'], queryFn: scheduleApi.templates });
  const apply = useMutation({
    mutationFn: (key: string) => scheduleApi.applyTemplate(projectId, key),
    onSuccess: onDone,
    onError: (error) =>
      onNotice(error instanceof ApiError ? error.message : 'Die Vorlage konnte nicht angewendet werden.'),
  });
  if ((templates.data ?? []).length === 0) return null;

  return (
    <Menu position="bottom-start" width={290}>
      <Menu.Target>
        <Button size={prominent ? 'sm' : 'xs'} variant={prominent ? 'filled' : 'subtle'} loading={apply.isPending}>
          {prominent ? 'Mit Vorlage beginnen' : 'Vorlage'}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {(templates.data ?? []).map((template) => (
          <Menu.Item key={template.key} onClick={() => apply.mutate(template.key)}>
            <Text size="sm" fw={500}>{template.title}</Text>
            <Text size="xs" c="dimmed">{template.description}</Text>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

/**
 * The list of relationships, folded away by default: the predecessors are
 * already visible as a column and as arrows. It stays for deleting one.
 */
function DependencySection({
  projectId,
  tasks,
  onDone,
}: {
  projectId: string;
  tasks: SchedTask[];
  onDone: () => Promise<void>;
}): JSX.Element | null {
  const [open, open$] = useDisclosure();
  const deps = useQuery({ queryKey: ['deps', projectId], queryFn: () => scheduleApi.dependencies(projectId) });
  const codeOf = new Map(tasks.map((task) => [task.id, task.code]));
  const list = deps.data ?? [];
  if (list.length === 0) return null;

  return (
    <Popover opened={open} onChange={open$.toggle} position="top-end" width={340} withArrow shadow="md">
      <Popover.Target>
        <Button size="compact-xs" variant="subtle" color="gray" onClick={open$.toggle}>
          Anordnungsbeziehungen ({list.length})
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Collapse in>
          <Stack gap={4} mah={320} style={{ overflowY: 'auto' }}>
            {list.map((dependency) => (
              <Group key={dependency.id} gap={6} wrap="nowrap">
                <Text size="sm" ff="monospace" w={110}>
                  {codeOf.get(dependency.predecessorId) ?? '?'} {'→'} {codeOf.get(dependency.successorId) ?? '?'}
                </Text>
                <Badge size="sm" variant="light" title={relationshipLabel(dependency.type)}>
                  {relationshipShort(dependency.type)}
                </Badge>
                <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                  {dependency.lagDays === 0 ? '' : `${dependency.lagDays > 0 ? '+' : ''}${dependency.lagDays} ${UNIT_DAYS}`}
                </Text>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="critical"
                  aria-label="Anordnungsbeziehung löschen"
                  onClick={async () => {
                    await scheduleApi.deleteDependency(projectId, dependency.id);
                    await onDone();
                  }}
                >
                  {'×'}
                </ActionIcon>
              </Group>
            ))}
          </Stack>
        </Collapse>
      </Popover.Dropdown>
    </Popover>
  );
}

// --- dialogs -------------------------------------------------------------

const phaseOptions = (nodes: WbsNode[]): { value: string; label: string }[] =>
  nodes.map((node) => ({ value: node.id, label: `${node.code}  ${node.name}` }));

function PhaseModal({
  projectId, opened, onClose, onDone, wbsNodes, presetParent,
}: {
  projectId: string; opened: boolean; onClose: () => void; onDone: () => Promise<void>; wbsNodes: WbsNode[]; presetParent?: string | null;
}): JSX.Element {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<string | null>(null);
  useEffect(() => {
    if (opened) setParentId(presetParent ?? null);
  }, [opened, presetParent]);
  const create = useMutation({
    mutationFn: () => scheduleApi.createWbs(projectId, { code, name, parentId }),
    onSuccess: async () => {
      await onDone();
      setCode(''); setName(''); onClose();
    },
  });
  return (
    <Modal opened={opened} onClose={onClose} title="Phase anlegen">
      <form onSubmit={(event) => { event.preventDefault(); create.mutate(); }}>
        <Stack>
          {create.isError && <Alert color="red">{create.error instanceof ApiError ? create.error.message : 'Anlegen fehlgeschlagen.'}</Alert>}
          <Text size="sm" c="dimmed">
            Eine Phase hat keine eigene Dauer. Ihr Zeitraum ergibt sich aus den Vorgängen darin.
          </Text>
          <TextInput label="Nummer" description="Gliederungsnummer, z. B. 3 oder 3.1" required data-autofocus value={code} onChange={(event) => setCode(event.currentTarget.value)} />
          <TextInput label="Bezeichnung" placeholder="z. B. Entwurfsplanung" required value={name} onChange={(event) => setName(event.currentTarget.value)} />
          <Select label="Übergeordnete Phase" placeholder="keine – oberste Ebene" clearable data={phaseOptions(wbsNodes)} value={parentId} onChange={setParentId} />
          <Button type="submit" loading={create.isPending}>Anlegen</Button>
        </Stack>
      </form>
    </Modal>
  );
}

/** Prefilled values when a task is created from a context menu. */
interface TaskPreset {
  code?: string;
  wbsNodeId?: string | null;
  /** Links the new task behind this one (Normalfolge). */
  predecessorId?: string;
  predecessorCode?: string;
}

function TaskModal({
  projectId, opened, onClose, onDone, wbsNodes, preset,
}: {
  projectId: string; opened: boolean; onClose: () => void; onDone: () => Promise<void>; wbsNodes: WbsNode[]; preset?: TaskPreset | null;
}): JSX.Element {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [duration, setDuration] = useState<number | string>(1);
  const [wbsNodeId, setWbsNodeId] = useState<string | null>(null);
  useEffect(() => {
    if (!opened) return;
    setCode(preset?.code ?? '');
    setWbsNodeId(preset?.wbsNodeId ?? null);
  }, [opened, preset]);
  const create = useMutation({
    mutationFn: async () => {
      const task = await scheduleApi.createTask(projectId, { code, name, durationDays: Number(duration), wbsNodeId });
      if (preset?.predecessorId) {
        await scheduleApi.createDependency(projectId, { predecessorId: preset.predecessorId, successorId: task.id, type: 'FS', lagDays: 0 });
      }
      return task;
    },
    onSuccess: async () => {
      await onDone();
      setCode(''); setName(''); setDuration(1); onClose();
    },
  });
  return (
    <Modal opened={opened} onClose={onClose} title="Vorgang anlegen">
      <form onSubmit={(event) => { event.preventDefault(); create.mutate(); }}>
        <Stack>
          {create.isError && <Alert color="red">{create.error instanceof ApiError ? create.error.message : 'Anlegen fehlgeschlagen.'}</Alert>}
          <TextInput label="Nummer" description="im Projekt eindeutig, z. B. 30 oder 3.2" required data-autofocus value={code} onChange={(event) => setCode(event.currentTarget.value)} />
          <TextInput label="Bezeichnung" required value={name} onChange={(event) => setName(event.currentTarget.value)} />
          <NumberInput label={`Dauer (${UNIT_DAYS})`} description="in Arbeitstagen; 0 legt einen Meilenstein an" min={0} allowDecimal={false} value={duration} onChange={setDuration} />
          <Select label="Phase" placeholder="keine" clearable data={phaseOptions(wbsNodes)} value={wbsNodeId} onChange={setWbsNodeId} />
          {preset?.predecessorCode && (
            <Text size="xs" c="dimmed">Wird als Nachfolger von {preset.predecessorCode} angelegt (Normalfolge).</Text>
          )}
          <Button type="submit" loading={create.isPending}>Anlegen</Button>
        </Stack>
      </form>
    </Modal>
  );
}

function DependencyModal({
  projectId, opened, onClose, onDone, tasks,
}: {
  projectId: string; opened: boolean; onClose: () => void; onDone: () => Promise<void>; tasks: SchedTask[];
}): JSX.Element {
  const [predecessorId, setPredecessorId] = useState<string | null>(null);
  const [successorId, setSuccessorId] = useState<string | null>(null);
  const [type, setType] = useState<string | null>('FS');
  const [lag, setLag] = useState<number | string>(0);
  const create = useMutation({
    mutationFn: () => scheduleApi.createDependency(projectId, { predecessorId, successorId, type, lagDays: Number(lag) }),
    onSuccess: async () => { await onDone(); onClose(); },
  });
  const options = [...tasks]
    .sort((a, b) => a.code.localeCompare(b.code, 'de', { numeric: true }))
    .map((task) => ({ value: task.id, label: `${task.code}  ${task.name}` }));
  return (
    <Modal opened={opened} onClose={onClose} title="Anordnungsbeziehung anlegen">
      <form onSubmit={(event) => { event.preventDefault(); create.mutate(); }}>
        <Stack>
          {create.isError && <Alert color="red" title="Nicht möglich">{create.error instanceof ApiError ? create.error.message : 'Anlegen fehlgeschlagen.'}</Alert>}
          <Select label="Vorgänger" required searchable data={options} value={predecessorId} onChange={setPredecessorId} />
          <Select label="Nachfolger" required searchable data={options} value={successorId} onChange={setSuccessorId} />
          <Select
            label="Beziehungsart"
            description="nach DIN 69900"
            data={(Object.keys(RELATIONSHIPS) as (keyof typeof RELATIONSHIPS)[]).map((code) => ({ value: code, label: relationshipLabel(code) }))}
            value={type}
            onChange={setType}
          />
          <NumberInput label={`Zeitabstand (${UNIT_DAYS})`} description="in Arbeitstagen; negativ bedeutet Überlappung" allowDecimal={false} value={lag} onChange={setLag} />
          <Button type="submit" loading={create.isPending}>Anlegen</Button>
        </Stack>
      </form>
    </Modal>
  );
}

/**
 * What a task edit has to write to take itself back. The shortcuts of the
 * table map to the stored fields: a start date is the "start no earlier
 * than" constraint, a finish date changed the duration.
 */

